import { ApolloClient, InMemoryCache, ApolloLink } from '@apollo/client'
import { createHttpLink } from '@apollo/client/link/http'
import { setContext } from '@apollo/client/link/context'
import { createUploadLink, isExtractableFile } from 'apollo-upload-client'
import { extractFiles } from 'extract-files'

import { isTokenExpired } from '../lib/utils'
import { refreshToken } from '../lib/providers/user'

const GRAPHQL_URI =
	process.env.REACT_APP_GRAPHQL_ENDPOINT || '/graphql'

/**
 * Plain JSON POST for queries and mutations without files. Using HttpLink for
 * these avoids multipart edge cases that can make Django/Graphene respond with
 * HTTP 400 "POST body sent invalid JSON."
 */
const httpLink = createHttpLink({
	uri: GRAPHQL_URI,
})

/** Multipart only when variables contain File/Blob (graphql-upload spec). */
const uploadLink = createUploadLink({
	uri: GRAPHQL_URI,
})

function operationHasUpload(operation) {
	const { files } = extractFiles(
		{ variables: operation.variables },
		'',
		isExtractableFile
	)
	return files.size > 0
}

const httpOrUploadLink = ApolloLink.split(
	operationHasUpload,
	uploadLink,
	httpLink
)

const authLink = setContext(async (req, { headers }) => {
	let currToken = localStorage.getItem('access_token')

	if (!currToken) return {}
	if (req.operationName === 'RefreshToken') return {}

	if (isTokenExpired(currToken)) {
		const refresh = localStorage.getItem('refresh_token')
		const { success, token, errors } = await refreshToken(refresh)

		if (success) {
			localStorage.setItem('access_token', token)
			currToken = token
		}

		if (errors) return {}
	}

	return {
		headers: {
			...headers,
			authorization: `Bearer ${currToken}`,
		},
	}
})

const defaultOptions = {
	query: {
		fetchPolicy: 'no-cache',
		errorPolicy: 'all',
	},
}

const client = new ApolloClient({
	link: authLink.concat(httpOrUploadLink),
	cache: new InMemoryCache(),
	defaultOptions,
})

export default client
